import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { 
  Upload, 
  Save, 
  X, 
  Search, 
  ChevronUp, 
  ChevronDown, 
  FileSpreadsheet,
  AlertCircle,
  RefreshCw,
  Trash2,
  CloudDownload
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

import LastUpdated from "../components/LastUpdated";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Column definitions for Sales Invoices
const columns = [
  { key: "invoice_number", label: "Invoice #", width: "120px", editable: false, type: "text" },
  { key: "customer", label: "Customer", width: "180px", editable: false, type: "text" },
  { key: "invoice_date", label: "Inv. Date", width: "110px", editable: false, type: "date" },
  { key: "due_date", label: "Due Date", width: "110px", editable: false, type: "date" },
  { key: "outstanding_amount", label: "Amount", width: "110px", editable: false, type: "currency" },
  { key: "overdue", label: "Overdue (Imported)", width: "100px", editable: false, type: "text" },
  { key: "payment_status", label: "Pay. Status", width: "120px", editable: true, type: "select", options: ["Niet betaald", "Betaald"] },
  { key: "overdue_status", label: "Overdue Status", width: "120px", editable: false, type: "status" },
  { key: "expected_payment_date", label: "Planned Pay.", width: "130px", editable: true, type: "date" },
  { key: "payment_term", label: "Payment term", width: "120px", editable: true, type: "text" },
  { key: "payment_date_adjusted", label: "Pay. date adjusted", width: "130px", editable: false, type: "text" },
  { key: "reminder_sent", label: "Reminder?", width: "100px", editable: true, type: "select", options: ["Ja", "Nee"] },
  { key: "action", label: "Action", width: "200px", editable: true, type: "text" },
  { key: "action_owner", label: "Owner", width: "120px", editable: true, type: "text" },
  { key: "action_status", label: "Action Status", width: "120px", editable: true, type: "select", options: ["-", "Open", "Mee bezig", "Gedaan"] },
];

const SalesInvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [dirtyInvoices, setDirtyInvoices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [filters, setFilters] = useState({ payment_status: "Niet betaald" });
  const [availableFilters, setAvailableFilters] = useState({});
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("due_date");
  const [sortOrder, setSortOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteInvoice, setDeleteInvoice] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filters.customer) params.append("customer", filters.customer);
      if (filters.payment_status) params.append("payment_status", filters.payment_status);
      if (filters.overdue_status) params.append("overdue_status", filters.overdue_status);
      if (filters.payment_method) params.append("payment_method", filters.payment_method);
      if (filters.action_owner) params.append("action_owner", filters.action_owner);
      if (filters.action_status) params.append("action_status", filters.action_status);
      params.append("sort_field", sortField);
      params.append("sort_order", sortOrder);
      params.append("page", page.toString());
      params.append("limit", "50");

      const response = await axios.get(`${API}/sales-invoices?${params.toString()}`);
      setInvoices(response.data.invoices);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
      setAvailableFilters(response.data.filters);
      setError(null);
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setError("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [search, filters, sortField, sortOrder, page]);

  useEffect(() => { document.title = "Sales Invoices - Floatly"; }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Get value (dirty or original)
  const getValue = (invoice, field) => {
    const dirtyData = dirtyInvoices[invoice.id];
    if (dirtyData && dirtyData[field] !== undefined) {
      return dirtyData[field];
    }
    return invoice[field];
  };

  // Handle cell change
  const handleCellChange = (invoiceId, field, value) => {
    setDirtyInvoices(prev => ({
      ...prev,
      [invoiceId]: {
        ...(prev[invoiceId] || {}),
        [field]: value
      }
    }));
  };

  // Count unsaved changes
  const unsavedCount = Object.keys(dirtyInvoices).length;

  // Save all changes
  const handleSave = async () => {
    if (unsavedCount === 0) return;
    
    setSaving(true);
    try {
      const updates = Object.entries(dirtyInvoices).map(([id, updates]) => ({
        id,
        updates
      }));

      await axios.patch(`${API}/sales-invoices/bulk`, { invoices: updates });
      setDirtyInvoices({});
      toast.success(`${updates.length} invoice(s) updated successfully`);
      fetchInvoices();
    } catch (err) {
      console.error("Error saving invoices:", err);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    setDirtyInvoices({});
    toast.info("Changes discarded");
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  // Handle file import
  const handleFileSelect = async (file) => {
    if (!file) {
      console.log("No file selected");
      return;
    }
    
    console.log("File selected:", file.name, file.size);
    
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["xls", "xlsx"].includes(ext)) {
      toast.error("Please select an Excel file (.xls or .xlsx)");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      console.log("Sending file to API...");
      const response = await axios.post(`${API}/sales-invoices/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      console.log("Import response:", response.data);
      toast.success(`Import successful: ${response.data.created} new, ${response.data.updated} updated${response.data.marked_paid ? `, ${response.data.marked_paid} marked as paid` : ""}`);
      setFilters({ payment_status: "Niet betaald" });
      setPage(1);
      fetchInvoices();
    } catch (err) {
      console.error("Error importing file:", err);
      toast.error(err.response?.data?.detail || "Failed to import file");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Sync from Exact Online
  const handleSyncExact = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/sales-invoices/sync-exact`);
      toast.success(`Exact sync: ${response.data.created} new, ${response.data.updated} updated${response.data.marked_paid ? `, ${response.data.marked_paid} marked as paid` : ""}`);
      setFilters({ payment_status: "Niet betaald" });
      setPage(1);
      fetchInvoices();
    } catch (err) {
      console.error("Error syncing from Exact:", err);
      toast.error(err.response?.data?.detail || "Failed to sync from Exact Online");
    } finally {
      setSyncing(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteInvoice) return;
    
    try {
      await axios.delete(`${API}/sales-invoices/${deleteInvoice.id}`);
      toast.success("Invoice deleted successfully");
      fetchInvoices();
    } catch (err) {
      console.error("Error deleting invoice:", err);
      toast.error("Failed to delete invoice");
    } finally {
      setDeleteInvoice(null);
    }
  };

  // Format display values
  const formatValue = (value, type) => {
    if (value === null || value === undefined || value === "") return "-";
    
    switch (type) {
      case "date":
        try {
          return format(parseISO(value), "dd/MM/yyyy");
        } catch {
          return value;
        }
      case "currency":
        return new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: "EUR"
        }).format(value);
      default:
        return value;
    }
  };

  // Get status color class
  const getStatusClass = (value, type) => {
    if (type === "status") {
      if (value === "Overdue") return "text-destructive font-medium";
      if (value === "Betaald") return "text-green-600 font-medium";
      return "text-muted-foreground";
    }
    if (type === "currency" && value < 0) {
      return "text-destructive";
    }
    return "";
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({});
    setSearch("");
    setPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(v => v) || search;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="error-state">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-2xl font-semibold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={fetchInvoices} data-testid="retry-button">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="sales-invoices-page">
      {/* Header */}
      <header className="border-b bg-white shrink-0">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="page-title">
                Sales Invoices
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {total} invoice{total !== 1 ? "s" : ""} 
                {unsavedCount > 0 && (
                  <span className="ml-2 text-accent font-medium">• {unsavedCount} unsaved</span>
                )}
              </p>
              <LastUpdated page="sales-invoices" />
            </div>
            
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  console.log("onChange triggered, file:", file);
                  if (file) {
                    handleFileSelect(file);
                  }
                }}
                onClick={(e) => {
                  e.target.value = null;
                }}
                style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, overflow: 'hidden' }}
                data-testid="file-input"
              />
              <Button
                variant="outline"
                onClick={handleSyncExact}
                disabled={syncing}
                className="rounded-full"
                data-testid="sync-exact-button"
              >
                <CloudDownload className={`w-4 h-4 mr-2 ${syncing ? 'animate-pulse' : ''}`} />
                {syncing ? "Syncing..." : "Sync Exact Online"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  console.log("Import button clicked, fileInputRef:", fileInputRef.current);
                  fileInputRef.current?.click();
                }}
                disabled={importing}
                className="rounded-full"
                data-testid="import-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? "Importing..." : "Import Excel"}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Drop Zone Overlay */}
      {isDragging && (
        <div 
          className="fixed inset-0 z-50 bg-primary/10 backdrop-blur-sm flex items-center justify-center"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="bg-white rounded-xl border-2 border-dashed border-primary p-12 text-center">
            <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-primary" />
            <p className="text-lg font-medium">Drop Excel file here</p>
            <p className="text-sm text-muted-foreground">.xls or .xlsx files only</p>
          </div>
        </div>
      )}

      {/* Filters Bar */}
      <div className="border-b bg-white shrink-0 px-6 py-3">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search invoice # or customer..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9"
              data-testid="search-input"
            />
          </div>

          {/* Filter Dropdowns */}
          <Select value={filters.customer || "all"} onValueChange={(v) => { setFilters(f => ({ ...f, customer: v === "all" ? undefined : v })); setPage(1); }}>
            <SelectTrigger className="w-[160px]" data-testid="filter-customer">
              <SelectValue placeholder="Customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {availableFilters.customers?.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.payment_status || "all"} onValueChange={(v) => { setFilters(f => ({ ...f, payment_status: v === "all" ? undefined : v })); setPage(1); }}>
            <SelectTrigger className="w-[140px]" data-testid="filter-payment-status">
              <SelectValue placeholder="Pay. Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {availableFilters.payment_statuses?.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.overdue_status || "all"} onValueChange={(v) => { setFilters(f => ({ ...f, overdue_status: v === "all" ? undefined : v })); setPage(1); }}>
            <SelectTrigger className="w-[140px]" data-testid="filter-overdue-status">
              <SelectValue placeholder="All Overdue" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Overdue</SelectItem>
              {availableFilters.overdue_statuses?.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.action_owner || "all"} onValueChange={(v) => { setFilters(f => ({ ...f, action_owner: v === "all" ? undefined : v })); setPage(1); }}>
            <SelectTrigger className="w-[140px]" data-testid="filter-action-owner">
              <SelectValue placeholder="All Owners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Owners</SelectItem>
              {availableFilters.action_owners?.map(o => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.action_status || "all"} onValueChange={(v) => { setFilters(f => ({ ...f, action_status: v === "all" ? undefined : v })); setPage(1); }}>
            <SelectTrigger className="w-[140px]" data-testid="filter-action-status">
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {availableFilters.action_statuses?.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground" data-testid="clear-filters">
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div 
        className="flex-1 overflow-auto"
        style={{ maxHeight: "calc(100vh - 220px)", paddingBottom: "160px" }}
        ref={dropZoneRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full" data-testid="loading-state">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-6" data-testid="empty-state">
            <FileSpreadsheet className="w-16 h-16 mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              No invoices found
            </h3>
            <p className="text-muted-foreground mb-6">
              {hasActiveFilters ? "Try adjusting your filters" : "Import an Excel file to get started"}
            </p>
            {!hasActiveFilters && (
              <Button onClick={() => fileInputRef.current?.click()} className="rounded-full" data-testid="import-cta">
                <Upload className="w-4 h-4 mr-2" />
                Import Excel
              </Button>
            )}
          </div>
        ) : (
            <table className="w-full border-collapse text-sm" style={{ minWidth: "2200px" }} data-testid="invoices-table">
                <thead className="sticky top-0 z-10 bg-white border-b">
                  <tr>
                    {columns.map(col => (
                      <th
                        key={col.key}
                        className="h-10 px-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors"
                        style={{ width: col.width, minWidth: col.width }}
                        onClick={() => handleSort(col.key)}
                        data-testid={`sort-${col.key}`}
                      >
                        <div className="flex items-center gap-1">
                          {col.label}
                          {sortField === col.key && (
                            sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="h-10 px-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice, idx) => {
                    const isDirty = !!dirtyInvoices[invoice.id];
                    const overdueStatus = getValue(invoice, "overdue_status");
                    const isOverdue = overdueStatus === "Overdue";
                    const isPaid = getValue(invoice, "payment_status") === "Betaald";
                    
                    return (
                      <tr
                        key={invoice.id}
                        className={`
                          border-b transition-colors
                          ${idx % 2 === 0 ? "bg-white" : "bg-muted/20"}
                          ${isDirty ? "bg-accent/5" : ""}
                          ${isOverdue && !isPaid ? "bg-red-50" : ""}
                          ${isPaid ? "bg-green-50/50" : ""}
                          hover:bg-muted/40
                        `}
                        data-testid={`row-${invoice.id}`}
                      >
                        {columns.map(col => {
                          const value = getValue(invoice, col.key);
                          const isEditable = col.editable;
                          
                          return (
                            <td
                              key={col.key}
                              className="h-12 px-3"
                              style={{ width: col.width, minWidth: col.width }}
                            >
                              {isEditable ? (
                                col.type === "select" ? (
                                  <Select
                                    value={value || ""}
                                    onValueChange={(v) => handleCellChange(invoice.id, col.key, v)}
                                  >
                                    <SelectTrigger className="h-8 text-xs border-0 bg-transparent hover:bg-white focus:bg-white" data-testid={`cell-${invoice.id}-${col.key}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {col.options?.map(opt => (
                                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : col.type === "date" ? (
                                  <Input
                                    type="date"
                                    value={value || ""}
                                    onChange={(e) => handleCellChange(invoice.id, col.key, e.target.value)}
                                    className="h-8 text-xs border-0 bg-transparent hover:bg-white focus:bg-white font-mono"
                                    data-testid={`cell-${invoice.id}-${col.key}`}
                                  />
                                ) : (
                                  <Input
                                    type="text"
                                    value={value || ""}
                                    onChange={(e) => handleCellChange(invoice.id, col.key, e.target.value)}
                                    className="h-8 text-xs border-0 bg-transparent hover:bg-white focus:bg-white"
                                    maxLength={col.key === "action" ? 150 : undefined}
                                    data-testid={`cell-${invoice.id}-${col.key}`}
                                  />
                                )
                              ) : (
                                <span className={`text-xs ${getStatusClass(value, col.type)} ${col.type === "currency" ? "font-mono" : ""}`}>
                                  {formatValue(value, col.type)}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className="h-12 px-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteInvoice(invoice)}
                            data-testid={`delete-${invoice.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t bg-white px-6 py-3 flex items-center justify-center gap-4 shrink-0" style={{ paddingBottom: "60px" }}>
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="prev-page"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              data-testid="next-page"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Floating Action Bar */}
      {unsavedCount > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white border rounded-full shadow-lg px-6 py-3 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4" data-testid="action-bar">
          <span className="text-sm text-muted-foreground">
            {unsavedCount} invoice{unsavedCount !== 1 ? "s" : ""} modified
          </span>
          <Button variant="outline" onClick={handleDiscard} className="rounded-full" data-testid="discard-button">
            <X className="w-4 h-4 mr-2" />
            Discard
          </Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-button">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteInvoice} onOpenChange={() => setDeleteInvoice(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice "{deleteInvoice?.invoice_number}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SalesInvoicesPage;
